import { createClient, Entry } from "contentful"
import Settings from "../../utils/CarrotConfig"
import { ICountry, IPageGuidance, ITalkToCarrotCountry, ITalkToCarrotLanguage, ITilePage } from "../../contentful-types"
import ContentTypes from "../../utils/ContentTypes"
import { nestedRefs } from "./utils/contentfulClientUtils"
import { reportError, reportErrorMessage } from "utils/ErrorReporting"
import L10n from "lib/carrot-l10n"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

enum StaticAssetID {
  TALK_TO_CARROT_LOCATION_GUIDANCE = "3ByERJy0buFOMf6f9BU6RQ",
  TALK_TO_CARROT_LANGUAGE_GUIDANCE = "1m5qiXAayKU40WZ2Ega5nE",
  TALK_TO_CARROT_NEED_HELP_GUIDANCE = "6HZUR0v8YJYCGeFG5ncCtm",
  TALK_TO_CARROT_PROVIDER_ELIGIBILITY = "3Tnl2nccRTKkk4oMC4ZmxB",
  TALK_TO_CARROT_REFRESH_CARROT_PLAN = "4oPn08X5v4mH61LsrqKT3H",
  TALK_TO_CARROT_GET_YOUR_CARROT_PLAN = "1n7FzTcMpdeJmMYir1FvHB",
  TALK_TO_CARROT_BENEFIT_CHAT_LANGUAGE_ENGLISH = "5M7VcFKp14yk41NLcyPYzg",
  TALK_TO_CARROT_DELETE_ACCOUNT_ENGLISH = "4jq2bKHVWVg85SMRK6XEGd"
}

const getContentfulClient = () =>
  createClient({
    space: Settings.CONTENTFUL_SPACE_ID,
    accessToken: Settings.CONTENTFUL_ACCESS_TOKEN,
    host: Settings.CONTENTFUL_PREVIEW_MODE === "true" ? "preview.contentful.com" : "cdn.contentful.com"
  })

const getTalkToCarrotLocations = async (): Promise<ITalkToCarrotCountry[]> => {
  const client = getContentfulClient()
  const { items } = await client.getEntries({
    content_type: ContentTypes.TALK_TO_CARROT_COUNTRY,
    include: 1
  })
  return items as ITalkToCarrotCountry[]
}

const getCountries = async (): Promise<ICountry[]> => {
  const client = getContentfulClient()
  const { items } = await client.getEntries({
    content_type: ContentTypes.COUNTRY,
    include: 1
  })
  return items as ICountry[]
}

const getTalkToCarrotLanguages = async (): Promise<ITalkToCarrotLanguage[]> => {
  const client = getContentfulClient()
  const { items } = await client.getEntries({
    content_type: ContentTypes.TALK_TO_CARROT_LANGUAGE,
    include: 1
  })
  return items as ITalkToCarrotLanguage[]
}

const getEntryById = async (id: string, locale: string): Promise<Entry<unknown>> => {
  const client = getContentfulClient()
  try {
    return await client.getEntry(id, { include: 10, locale })
  } catch (err) {
    if (err && err.sys) {
      const {
        sys: { id: errorId }
      } = err
      if (errorId === "NotFound") {
        reportErrorMessage(
          `Contentful resource not found for entityId: ${id}, this may just be a work in progress and in a draft state`
        )
        return null
      }
    }
    reportError(err)
    return null
  }
}

const getTilePageById = async (id: string, locale: string): Promise<ITilePage> => {
  const result = (await getEntryById(id, locale)) as ITilePage
  await nestedRefs(result, getEntryById)
  return result
}

const getGuidance = async (entryId: StaticAssetID, locale: string): Promise<IPageGuidance> => {
  const item = (await getEntryById(entryId, locale)) as IPageGuidance
  if (!item) {
    reportErrorMessage(`No Contentful page guidance for entityId: ${entryId}`)
  }
  return item
}

const getTalkToCarrotLanguageGuidance = async (locale: string): Promise<IPageGuidance> =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_LANGUAGE_GUIDANCE, locale)

const getTalkToCarrotLocationGuidance = async (locale: string): Promise<IPageGuidance> =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_LOCATION_GUIDANCE, locale)

const getTalkToCarrotNeedHelpGuidance = async (locale: string): Promise<IPageGuidance> =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_NEED_HELP_GUIDANCE, locale)

const getTalkToCarrotProviderEligibilityGuidance = async (locale: string): Promise<IPageGuidance> =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_PROVIDER_ELIGIBILITY, locale)

const getTalkToCarrotRefreshCarrotPlanGuidance = async (locale: string): Promise<IPageGuidance> =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_REFRESH_CARROT_PLAN, locale)

const getTalkToCarrotGetYourCarrotPlanGuidance = async (locale: string): Promise<IPageGuidance> =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_GET_YOUR_CARROT_PLAN, locale)

const getTalkToCarrotDeleteAccountGuidance = async (locale: string): Promise<IPageGuidance> =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_DELETE_ACCOUNT_ENGLISH, locale)

const getTalkToCarrotSendAMessagePageGuidance = async (): Promise<IPageGuidance> => {
  // This is faking the contentful call like the other helper function above
  // Eventually we probably want to align on what we want to do here.
  const title = l(`ttc.send-a-message-page.sendMessagePage.heading`)
  const description = l(`ttc.send-a-message-page.sendMessagePage.description`)
  return Promise.resolve(({ fields: { title, description } } as unknown) as IPageGuidance)
}

export {
  StaticAssetID,
  getContentfulClient,
  getEntryById,
  getTalkToCarrotLocations,
  getTalkToCarrotLocationGuidance,
  getTilePageById,
  getTalkToCarrotNeedHelpGuidance,
  getTalkToCarrotProviderEligibilityGuidance,
  getTalkToCarrotRefreshCarrotPlanGuidance,
  getTalkToCarrotGetYourCarrotPlanGuidance,
  getTalkToCarrotSendAMessagePageGuidance,
  getTalkToCarrotLanguageGuidance,
  getCountries,
  getTalkToCarrotLanguages,
  getTalkToCarrotDeleteAccountGuidance
}
